import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import toast from 'react-hot-toast';
import type { Academy } from '@court-sync/shared';
import { academyFormSchema, type AcademyFormData, type AcademyUpdateRequest, TierFeatureKey } from '@court-sync/shared';
import { Button, Card, CardContent, Tooltip } from '@/components/ui';
import { LessonSettingsTab } from '@/components/academy/LessonSettingsTab';
import { LessonProfessionalFeaturesLockedCard } from '@/components/academy/LessonProfessionalFeaturesLockedCard';
import { Skeleton } from '@/components/ui/Skeleton';
import { useManagementDashboardFeatureAccess } from '@/hooks/useFeatureAccess';
import { updateAcademyQuery } from '@/queries/academy';
import { useSubscriptionCheck } from '@/hooks/useSubscriptionCheck';

interface AcademyLessonSettingsSectionProps {
  academy: Academy;
  onSaved?: (academy: Academy) => void;
}

export function AcademyLessonSettingsSection({ academy, onSaved }: AcademyLessonSettingsSectionProps) {
  const { hasValidSubscription, tooltipMessage } = useSubscriptionCheck();
  const { hasAccess: hasProfessionalLessonSettings, tierResolved } =
    useManagementDashboardFeatureAccess(TierFeatureKey.MAKEUP_CREDITS);

  const {
    register,
    handleSubmit,
    control,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<AcademyFormData>({
    resolver: zodResolver(academyFormSchema),
    defaultValues: {
      name: academy.name,
      slug: academy.slug,
      minimumStudentsForLesson: academy.minimumStudentsForLesson ?? undefined,
      lowEnrollmentAlertsEnabled:
        academy.lowEnrollmentAlertsEnabled ??
        !!(academy.minimumStudentsForLesson != null && academy.minimumStudentsForLesson > 0),
      makeupLessonEnabled: academy.makeupLessonEnabled ?? false,
      makeupNotificationDeadlineHours: academy.makeupNotificationDeadlineHours ?? 24,
      makeupCreditExpiryDays: academy.makeupCreditExpiryDays ?? 30,
      makeupCreditExpiryMode: academy.makeupCreditExpiryMode ?? 'days',
      makeupCreditMaxPerPeriod: academy.makeupCreditMaxPerPeriod ?? undefined,
      makeupCreditMaxPeriodMode: academy.makeupCreditMaxPeriodMode ?? 'days',
      makeupCreditMaxPeriodDays: academy.makeupCreditMaxPeriodDays ?? 30,
      waitlistEnabled: academy.waitlistEnabled ?? false,
      waitlistExpiryHours: academy.waitlistExpiryHours ?? 24,
      smsEnabled: academy.smsEnabled ?? false,
      allowEnrolmentWhenFull: academy.allowEnrolmentWhenFull ?? false,
      proRataEnabled: academy.proRataEnabled ?? false,
      allowCoachSelfAttendance: academy.allowCoachSelfAttendance ?? false,
      privateBookingEnabled: academy.privateBookingEnabled ?? false,
      privateBookingMinDurationMinutes: academy.privateBookingMinDurationMinutes ?? 60,
      privateBookingMaxDurationMinutes: academy.privateBookingMaxDurationMinutes ?? 120,
      privateBookingDurationStepMinutes: academy.privateBookingDurationStepMinutes ?? 30,
      privateBookingApprovalRequired: academy.privateBookingApprovalRequired !== false,
      privateBookingMinLeadHours: academy.privateBookingMinLeadHours ?? 24,
    },
  });

  useEffect(() => {
    reset({
      name: academy.name,
      slug: academy.slug,
      minimumStudentsForLesson: academy.minimumStudentsForLesson ?? undefined,
      lowEnrollmentAlertsEnabled:
        academy.lowEnrollmentAlertsEnabled ??
        !!(academy.minimumStudentsForLesson != null && academy.minimumStudentsForLesson > 0),
      makeupLessonEnabled: academy.makeupLessonEnabled ?? false,
      makeupNotificationDeadlineHours: academy.makeupNotificationDeadlineHours ?? 24,
      makeupCreditExpiryDays: academy.makeupCreditExpiryDays ?? 30,
      makeupCreditExpiryMode: academy.makeupCreditExpiryMode ?? 'days',
      makeupCreditMaxPerPeriod: academy.makeupCreditMaxPerPeriod ?? undefined,
      makeupCreditMaxPeriodMode: academy.makeupCreditMaxPeriodMode ?? 'days',
      makeupCreditMaxPeriodDays: academy.makeupCreditMaxPeriodDays ?? 30,
      waitlistEnabled: academy.waitlistEnabled ?? false,
      waitlistExpiryHours: academy.waitlistExpiryHours ?? 24,
      smsEnabled: academy.smsEnabled ?? false,
      allowEnrolmentWhenFull: academy.allowEnrolmentWhenFull ?? false,
      proRataEnabled: academy.proRataEnabled ?? false,
      allowCoachSelfAttendance: academy.allowCoachSelfAttendance ?? false,
      privateBookingEnabled: academy.privateBookingEnabled ?? false,
      privateBookingMinDurationMinutes: academy.privateBookingMinDurationMinutes ?? 60,
      privateBookingMaxDurationMinutes: academy.privateBookingMaxDurationMinutes ?? 120,
      privateBookingDurationStepMinutes: academy.privateBookingDurationStepMinutes ?? 30,
      privateBookingApprovalRequired: academy.privateBookingApprovalRequired !== false,
      privateBookingMinLeadHours: academy.privateBookingMinLeadHours ?? 24,
    });
  }, [academy, reset]);

  const onSubmit = async (data: AcademyFormData) => {
    const academyData: AcademyUpdateRequest = {
      minimumStudentsForLesson: data.minimumStudentsForLesson ?? (null as AcademyUpdateRequest['minimumStudentsForLesson']),
      lowEnrollmentAlertsEnabled: data.lowEnrollmentAlertsEnabled ?? false,
      makeupLessonEnabled: data.makeupLessonEnabled ?? false,
      makeupNotificationDeadlineHours: data.makeupNotificationDeadlineHours ?? (null as AcademyUpdateRequest['makeupNotificationDeadlineHours']),
      makeupCreditExpiryDays: data.makeupCreditExpiryDays ?? (null as AcademyUpdateRequest['makeupCreditExpiryDays']),
      makeupCreditExpiryMode: data.makeupCreditExpiryMode ?? (null as AcademyUpdateRequest['makeupCreditExpiryMode']),
      makeupCreditMaxPerPeriod:
        data.makeupCreditMaxPerPeriod != null && data.makeupCreditMaxPerPeriod >= 1
          ? data.makeupCreditMaxPerPeriod
          : (null as AcademyUpdateRequest['makeupCreditMaxPerPeriod']),
      makeupCreditMaxPeriodMode:
        data.makeupCreditMaxPerPeriod != null && data.makeupCreditMaxPerPeriod >= 1
          ? data.makeupCreditMaxPeriodMode ?? 'days'
          : (null as AcademyUpdateRequest['makeupCreditMaxPeriodMode']),
      makeupCreditMaxPeriodDays:
        data.makeupCreditMaxPerPeriod != null && data.makeupCreditMaxPerPeriod >= 1
          ? data.makeupCreditMaxPeriodDays ?? 30
          : (null as AcademyUpdateRequest['makeupCreditMaxPeriodDays']),
      waitlistEnabled: data.waitlistEnabled ?? false,
      waitlistExpiryHours: data.waitlistExpiryHours ?? (null as AcademyUpdateRequest['waitlistExpiryHours']),
      smsEnabled: data.smsEnabled ?? false,
      allowEnrolmentWhenFull: data.allowEnrolmentWhenFull ?? false,
      proRataEnabled: data.proRataEnabled ?? false,
      allowCoachSelfAttendance: data.allowCoachSelfAttendance ?? false,
      privateBookingEnabled: data.privateBookingEnabled ?? false,
      privateBookingMinDurationMinutes: data.privateBookingMinDurationMinutes ?? 60,
      privateBookingMaxDurationMinutes: data.privateBookingMaxDurationMinutes ?? 120,
      privateBookingDurationStepMinutes: data.privateBookingDurationStepMinutes ?? 30,
      privateBookingApprovalRequired: data.privateBookingApprovalRequired !== false,
      privateBookingMinLeadHours: data.privateBookingMinLeadHours ?? 24,
    };

    try {
      const updated = await updateAcademyQuery(academy.id, academyData);
      toast.success('Advanced settings updated');
      onSaved?.(updated);
    } catch (error) {
      console.error(error);
      toast.error(error instanceof Error ? error.message : 'Failed to update advanced settings');
    }
  };

  if (!tierResolved) {
    return <Skeleton className="h-64 w-full rounded-xl" aria-label="Loading plan" />;
  }

  if (!hasProfessionalLessonSettings) {
    return <LessonProfessionalFeaturesLockedCard />;
  }

  return (
    <Card className="overflow-hidden">
      <CardContent className="p-0">
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="px-4 py-6 sm:px-6">
            <LessonSettingsTab register={register} control={control} watch={watch} errors={errors} />
          </div>
          <div className="flex justify-end border-t border-border bg-muted/10 px-4 py-4 sm:px-6">
            <Tooltip content={tooltipMessage} disabled={hasValidSubscription}>
              <Button type="submit" isLoading={isSubmitting} disabled={!hasValidSubscription}>
                Save advanced settings
              </Button>
            </Tooltip>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
